import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useMyRoutes } from "@/hooks/useMyRoutes";
import { useGeolocation } from "@/hooks/useGeolocation";
import { useVisitedVenues } from "@/hooks/useVisitedVenues";
import { useRouteInvites } from "@/hooks/useRouteInvites";
import { supabase } from "@/lib/supabase";
import RouteBuilderModal from "../components/RouteBuilderModal";
import RouteTrackerModal from "../components/RouteTrackerModal";
import RouteInviteModal from "../components/RouteInviteModal";
import RouteStatsModal from "../components/RouteStatsModal";
import type { UserRoute, RouteStop } from "@/hooks/useMyRoutes";

interface Props { userId: string; username: string; }

interface PartnerProfile {
  id: string;
  username: string;
  display_name: string | null;
  avatar_url: string | null;
}

function usePartnerProfiles(routes: UserRoute[]) {
  const [partners, setPartners] = useState<Record<string, PartnerProfile>>({});

  useEffect(() => {
    const ids = [...new Set(routes.map((r) => r.partnerUserId).filter(Boolean) as string[])];
    if (ids.length === 0) return;
    supabase
      .from("profiles")
      .select("id, username, display_name, avatar_url")
      .in("id", ids)
      .then(({ data }) => {
        const map: Record<string, PartnerProfile> = {};
        (data ?? []).forEach((p: PartnerProfile) => { map[p.id] = p; });
        setPartners(map);
      });
  }, [routes]);

  return partners;
}

export default function MyRoutesSection({ userId }: Props) {
  const geo = useGeolocation();
  const { routes, createRoute, deleteRoute, startTracking, stopTracking, checkAndCompleteStop, refetch } = useMyRoutes(userId);
  const { markVisited } = useVisitedVenues(userId);
  const { invites, loading: invitesLoading, acceptInvite, declineInvite } = useRouteInvites(userId);
  const partnerProfiles = usePartnerProfiles(routes);

  const [showBuilder, setShowBuilder] = useState(false);
  const [trackingRoute, setTrackingRoute] = useState<UserRoute | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [inviteRoute, setInviteRoute] = useState<UserRoute | null>(null);
  const [statsRoute, setStatsRoute] = useState<UserRoute | null>(null);
  const [acceptingId, setAcceptingId] = useState<string | null>(null);
  const [decliningId, setDecliningId] = useState<string | null>(null);

  const handleStartTracking = async (route: UserRoute) => {
    if (geo.lat && geo.lng) {
      await startTracking(route.id, geo.lat, geo.lng);
    } else {
      geo.requestLocation();
    }
    setTrackingRoute(route);
  };

  const handleCheckComplete = (routeId: string) => async (venueId: string, lat: number, lng: number) => {
    return checkAndCompleteStop(routeId, venueId, lat, lng);
  };

  const handleVenueCompleted = async (stop: RouteStop) => {
    await markVisited(stop.venueId, stop.venueName, stop.category, stop.image);
  };

  const handleAcceptInvite = async (inviteId: string) => {
    const invite = invites.find((i) => i.id === inviteId);
    if (!invite) return;
    setAcceptingId(inviteId);
    await acceptInvite(invite, userId);
    await refetch();
    setAcceptingId(null);
  };

  const handleDeclineInvite = async (inviteId: string) => {
    setDecliningId(inviteId);
    await declineInvite(inviteId);
    setDecliningId(null);
  };

  const liveRoute = trackingRoute ? routes.find((r) => r.id === trackingRoute.id) ?? trackingRoute : null;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-bold text-stone-900 text-2xl">Rotalarım</h2>
          <p className="text-stone-500 text-sm mt-1">Kendi rotalarını oluştur, takip et ve tamamla.</p>
        </div>
        <button
          onClick={() => setShowBuilder(true)}
          className="px-5 py-2.5 bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl text-sm font-semibold cursor-pointer whitespace-nowrap transition-colors inline-flex items-center gap-2"
        >
          <i className="ri-add-line" /> Yeni Rota
        </button>
      </div>

      {/* Pending Invites Banner */}
      {!invitesLoading && invites.length > 0 && (
        <div className="mb-6 space-y-3">
          <div className="flex items-center gap-2 mb-2">
            <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
            <p className="text-sm font-semibold text-stone-800">
              {invites.length} bekleyen rota daveti
            </p>
          </div>
          {invites.map((invite) => (
            <div
              key={invite.id}
              className="bg-amber-50 border border-amber-200 rounded-2xl p-4"
            >
              <div className="flex items-start gap-3">
                {invite.inviterAvatar ? (
                  <img
                    src={invite.inviterAvatar}
                    alt={invite.inviterName}
                    className="w-10 h-10 rounded-full object-cover object-top flex-shrink-0 border border-amber-200"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold text-sm">
                      {invite.inviterName.charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-stone-900">
                    <Link
                      to={`/profile/${invite.inviterUsername}`}
                      className="hover:text-emerald-700 transition-colors"
                    >
                      {invite.inviterName}
                    </Link>{" "}
                    seni rotaya davet etti!
                  </p>
                  <div className="flex items-center gap-2 mt-1">
                    <i className="ri-route-line text-amber-600 text-xs" />
                    <span className="text-xs text-amber-800 font-medium truncate">{invite.routeName}</span>
                    <span className="text-xs text-amber-600">· {invite.stopCount} durak</span>
                  </div>
                  <p className="text-xs text-amber-600 mt-1.5 flex items-center gap-1">
                    <i className="ri-team-line" />
                    Kabul edersen bu rota her iki profilinizde birlikte görünür
                  </p>
                </div>
              </div>
              <div className="flex gap-2 mt-3">
                <button
                  onClick={() => handleDeclineInvite(invite.id)}
                  disabled={decliningId === invite.id || acceptingId === invite.id}
                  className="flex-1 py-2 border border-stone-300 text-stone-600 hover:bg-stone-50 rounded-xl text-xs font-semibold cursor-pointer whitespace-nowrap transition-colors disabled:opacity-60"
                >
                  {decliningId === invite.id ? (
                    <i className="ri-loader-4-line animate-spin" />
                  ) : "Reddet"}
                </button>
                <button
                  onClick={() => handleAcceptInvite(invite.id)}
                  disabled={acceptingId === invite.id || decliningId === invite.id}
                  className="flex-1 py-2 bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl text-xs font-semibold cursor-pointer whitespace-nowrap transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-1.5"
                >
                  {acceptingId === invite.id ? (
                    <><i className="ri-loader-4-line animate-spin" /> Ekleniyor...</>
                  ) : (
                    <><i className="ri-check-line" /> Kabul Et</>
                  )}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {routes.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-stone-200 rounded-2xl">
          <div className="w-14 h-14 rounded-full bg-stone-100 flex items-center justify-center mx-auto mb-3">
            <i className="ri-route-line text-stone-400 text-2xl" />
          </div>
          <p className="text-stone-700 font-semibold mb-1">Henüz rota oluşturmadınız</p>
          <p className="text-stone-400 text-sm mb-5">Mekanları seçerek kendi Bursa rotanı oluştur!</p>
          <button
            onClick={() => setShowBuilder(true)}
            className="px-6 py-2.5 bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl text-sm font-semibold cursor-pointer whitespace-nowrap transition-colors"
          >
            İlk Rotamı Oluştur
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {routes.map((route) => {
            const completed = route.stops.filter((s) => s.completed).length;
            const total = route.stops.length;
            const progress = total > 0 ? (completed / total) * 100 : 0;
            const partner = route.partnerUserId ? partnerProfiles[route.partnerUserId] : null;

            return (
              <div key={route.id} className="border border-stone-200 rounded-2xl p-5 bg-white">
                <div className="flex items-start justify-between mb-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-bold text-stone-900 truncate">{route.name}</h3>
                      {partner && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-teal-50 border border-teal-200 rounded-full text-xs text-teal-700 font-medium whitespace-nowrap flex-shrink-0">
                          <i className="ri-team-line" /> Birlikte
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-stone-500 mt-0.5">
                      {total} mekan · {new Date(route.createdAt).toLocaleDateString("tr-TR")}
                    </p>
                    {partner && (
                      <Link
                        to={`/profile/${partner.username}`}
                        className="inline-flex items-center gap-1.5 mt-1.5 group"
                      >
                        {partner.avatar_url ? (
                          <img
                            src={partner.avatar_url}
                            alt={partner.display_name || partner.username}
                            className="w-5 h-5 rounded-full object-cover object-top border border-teal-200"
                          />
                        ) : (
                          <div className="w-5 h-5 rounded-full bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center">
                            <span className="text-white font-bold text-xs">
                              {(partner.display_name || partner.username).charAt(0).toUpperCase()}
                            </span>
                          </div>
                        )}
                        <span className="text-xs text-teal-700 group-hover:underline font-medium">
                          @{partner.username} ile birlikte
                        </span>
                      </Link>
                    )}
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={() => setStatsRoute(route)}
                      title="Rota İstatistikleri"
                      className="w-8 h-8 flex items-center justify-center text-stone-500 hover:bg-stone-100 rounded-lg cursor-pointer transition-colors"
                    >
                      <i className="ri-bar-chart-2-line text-sm" />
                    </button>
                    <button
                      onClick={() => setInviteRoute(route)}
                      title="Arkadaşını Davet Et"
                      className="w-8 h-8 flex items-center justify-center text-emerald-600 hover:bg-emerald-50 rounded-lg cursor-pointer transition-colors"
                    >
                      <i className="ri-user-add-line text-sm" />
                    </button>
                    <button
                      onClick={() => setDeleteConfirm(route.id)}
                      className="w-8 h-8 flex items-center justify-center text-stone-400 hover:text-red-500 hover:bg-red-50 rounded-lg cursor-pointer transition-colors"
                    >
                      <i className="ri-delete-bin-line" />
                    </button>
                  </div>
                </div>

                {/* Progress */}
                <div className="mb-3">
                  <div className="flex justify-between text-xs text-stone-500 mb-1.5">
                    <span>İlerleme</span>
                    <span>{completed}/{total} tamamlandı</span>
                  </div>
                  <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-emerald-500 rounded-full transition-all"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                </div>

                {/* Stop thumbnails */}
                <div className="flex -space-x-2 mb-4">
                  {route.stops.slice(0, 5).map((stop) => (
                    <div
                      key={stop.venueId}
                      className={`w-8 h-8 rounded-full border-2 overflow-hidden flex-shrink-0 ${stop.completed ? "border-emerald-400" : "border-white"}`}
                    >
                      <img
                        src={stop.image}
                        alt={stop.venueName}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                  ))}
                  {total > 5 && (
                    <div className="w-8 h-8 rounded-full border-2 border-white bg-stone-200 flex items-center justify-center text-xs font-bold text-stone-600 flex-shrink-0">
                      +{total - 5}
                    </div>
                  )}
                  {partner && (
                    <div className="ml-3 pl-3 border-l border-stone-200 flex items-center">
                      {partner.avatar_url ? (
                        <img
                          src={partner.avatar_url}
                          alt={partner.display_name || partner.username}
                          className="w-8 h-8 rounded-full object-cover object-top border-2 border-teal-300"
                        />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center border-2 border-teal-300">
                          <span className="text-white font-bold text-xs">
                            {(partner.display_name || partner.username).charAt(0).toUpperCase()}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2">
                  <button
                    onClick={() => handleStartTracking(route)}
                    className="flex-1 py-2.5 bg-stone-900 hover:bg-stone-700 text-white rounded-xl text-sm font-semibold cursor-pointer whitespace-nowrap transition-colors inline-flex items-center justify-center gap-2"
                  >
                    <i className={route.isTracking ? "ri-navigation-fill text-emerald-400" : "ri-play-line"} />
                    {route.isTracking ? "Takibe Devam" : "Rotayı Başlat"}
                  </button>
                  <button
                    onClick={() => setStatsRoute(route)}
                    className="px-3 py-2.5 border border-stone-200 text-stone-600 hover:bg-stone-50 rounded-xl text-sm font-semibold cursor-pointer whitespace-nowrap transition-colors inline-flex items-center gap-1.5"
                    title="İstatistikler"
                  >
                    <i className="ri-bar-chart-2-line" />
                  </button>
                  <button
                    onClick={() => setInviteRoute(route)}
                    className="px-3 py-2.5 border border-emerald-200 text-emerald-700 hover:bg-emerald-50 rounded-xl text-sm font-semibold cursor-pointer whitespace-nowrap transition-colors inline-flex items-center gap-1.5"
                  >
                    <i className="ri-user-add-line" />
                    <span className="hidden sm:inline">Davet</span>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Delete confirm */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setDeleteConfirm(null)} />
          <div className="relative bg-white rounded-2xl p-6 max-w-sm w-full">
            <h3 className="font-bold text-stone-900 mb-2">Rotayı Sil</h3>
            <p className="text-stone-500 text-sm mb-5">Bu rota kalıcı olarak silinecek. Emin misiniz?</p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 py-2.5 border border-stone-200 rounded-xl text-sm font-semibold text-stone-600 hover:bg-stone-50 cursor-pointer whitespace-nowrap"
              >
                İptal
              </button>
              <button
                onClick={() => { deleteRoute(deleteConfirm); setDeleteConfirm(null); }}
                className="flex-1 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl text-sm font-semibold cursor-pointer whitespace-nowrap"
              >
                Sil
              </button>
            </div>
          </div>
        </div>
      )}

      {showBuilder && (
        <RouteBuilderModal
          onClose={() => setShowBuilder(false)}
          onSave={(name, stops) => createRoute(name, stops)}
        />
      )}

      {liveRoute && (
        <RouteTrackerModal
          route={liveRoute}
          onClose={() => setTrackingRoute(null)}
          onStopTracking={() => { stopTracking(liveRoute.id); setTrackingRoute(null); }}
          onCheckComplete={handleCheckComplete(liveRoute.id)}
          onVenueCompleted={handleVenueCompleted}
        />
      )}

      {inviteRoute && (
        <RouteInviteModal
          routeId={inviteRoute.id}
          routeName={inviteRoute.name}
          routeStops={inviteRoute.stops}
          onClose={() => setInviteRoute(null)}
        />
      )}

      {statsRoute && (
        <RouteStatsModal
          route={statsRoute}
          onClose={() => setStatsRoute(null)}
        />
      )}
    </div>
  );
}
